import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Bot, Building2, User, Pencil, Play, Square, MessageSquare } from '../../icons';
import { Badge, Button, Card, EmptyState, PageHeader, Tabs } from '../../components/shared';
import { layerConfirm } from '../../components/LayerPopup/LayerPopup';
import { AI_MODEL_ALIAS_LABEL, isAiModelAlias } from '@workchat/shared-types';
import { AI_STARTER_DOMAINS } from '../../lib/ai-starters';
import {
  listAiAgents, updateAiAgent, type AiAgent, type AiAgentFilter,
} from '../../api/assist-agents';
import { useAiChatStore } from '../../store/aiChatStore';
import { AI_HOME_PATH } from '../../lib/ai-menu';
import './AiShell.css';

/**
 * AI · 에이전트 — 역할·규칙을 정해 두고 그 역할로 묻는 자리 (설계 판 E · 정의서 A1~A9).
 *
 * ## ★★ 「검색 데이터」는 판 E-② 에서 «길이 나서» 생겼다
 *
 * 판 E 를 세울 때는 게이트웨이가 그 뜻을 orchestrator 까지 못 날라 **일부러 안 그렸다**.
 * 이제 `tool_domains` 가 chat-server → gateway-ai → orchestrator 를 통과하므로 폼에 절이 섰고,
 * 목록은 **좁혀 둔 것만** 적는다(전부는 모두에게 늘 같은 값이라 자리만 먹는다).
 * ⚠ 남은 하나 — **배경 지식 파일**은 여전히 안 그린다(올려도 읽어 답에 싣는 곳이 없다).
 * **켤 수 없는 스위치를 켜진 것처럼 그리지 않는다** — 「웹」 토글을 안 그린 것과 같은 판단이다.
 *
 * ## ★★★ 권한 칸이 없는 이유
 *
 * 도구는 언제나 **부른 사람 명의**로 돈다(`ai-actor.ts`). 전사 에이전트로 물어도 그 사람이
 * 못 보는 데이터는 안 나온다 — 에이전트는 «무엇을 물을지»를 정할 뿐이다.
 *
 * ## 없애기 = 중지 (A8 · 정본 P7)
 *
 * 지난 대화가 「이 답은 어느 에이전트가 했나」에 답하려면 남아 있어야 한다. 그래서 목록의
 * 행동은 「중지」이고, 중지된 것은 만든 사람(전사는 관리자)에게만 흐리게 남는다.
 */

/** 도메인 키 → 사람이 읽는 이름. 모르는 키는 그대로 둔다(서버가 늘렸을 수 있다). */
function domainLabels(keys: readonly string[]): string {
  const byKey = new Map(AI_STARTER_DOMAINS.map((d) => [d.key, d.label]));
  return keys.map((k) => byKey.get(k) ?? k).join(' · ');
}

const FILTERS: ReadonlyArray<{ value: AiAgentFilter; label: string }> = [
  { value: 'all', label: '전체' },
  { value: 'tenant', label: '전사 에이전트' },
  { value: 'mine', label: '내가 만든 에이전트' },
];

export function AiAgentsPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<AiAgentFilter>('all');
  const [items, setItems] = useState<AiAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const setAgent = useAiChatStore((s) => s.setAgent);
  const current = useAiChatStore((s) => s.agent);

  const load = useCallback(async (f: AiAgentFilter) => {
    setLoading(true);
    setFailed(false);
    try {
      setItems(await listAiAgents(f));
    } catch {
      /* 「없다」와 「못 읽었다」는 다른 말이다 — 빈 목록으로 얼버무리지 않는다. */
      setFailed(true);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(filter); }, [filter, load]);

  /** 이 에이전트로 묻는다 — 고른 뒤 「대화」로 간다(물을 자리가 거기다).
      ⚠ 이름을 `use…` 로 짓지 않는다 — 훅으로 읽혀 lint 가 조건부 호출로 본다. */
  function pickAgent(a: AiAgent): void {
    setAgent(a);
    navigate(AI_HOME_PATH);
  }

  async function toggleStatus(a: AiAgent): Promise<void> {
    const stopping = a.status === 'ACTIVE';
    if (stopping) {
      /* ★ 「중지」가 무엇을 뜻하는지 먼저 말한다 — 지우는 것이 아니라 새 대화에 안 쓰이는 것이다. */
      const ok = await layerConfirm(
        `「${a.name}」 에이전트를 중지할까요?\n새 대화에는 쓰이지 않습니다. 지난 대화는 그대로 남습니다.`,
      );
      if (!ok) return;
    }
    const next = await updateAiAgent(a.id, { status: stopping ? 'STOPPED' : 'ACTIVE' });
    setItems((xs) => xs.map((x) => (x.id === next.id ? next : x)));
    /* 지금 고른 것을 중지했으면 화면도 놓는다 — 이름은 떠 있는데 답은 그 역할이 아니게 된다. */
    if (stopping && current?.id === a.id) setAgent(null);
  }

  return (
    <div className="aiagents">
      <PageHeader
        breadcrumb={['AI', '에이전트']}
        title="에이전트"
        description="역할과 답변 규칙을 정해 두고, 그 역할로 묻습니다."
        actions={
          <Button variant="primary" iconLeft={<Plus size={15} />} onClick={() => navigate('/ai/agents/new')}>
            새 에이전트
          </Button>
        }
        help={{
          body:
            '에이전트는 «무엇을 물을지»를 정합니다. «무엇을 볼 수 있는지»는 언제나 로그인한 사람의 권한을 따르므로, '
            + '전사 에이전트로 물어도 내가 볼 수 없는 자료는 나오지 않습니다.',
        }}
      />

      <div className="aiagents__col">
        <Tabs
          items={FILTERS.map((f) => ({ value: f.value, label: f.label }))}
          value={filter}
          onChange={(v) => setFilter(v as AiAgentFilter)}
          ariaLabel="에이전트 거르개"
        />

        {loading ? (
          <p className="aiagents__note">불러오는 중입니다…</p>
        ) : failed ? (
          <p className="aiagents__note" role="alert">
            목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.
          </p>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Bot}
            title="아직 에이전트가 없습니다"
            description="역할과 답변 규칙을 정해 두면, 같은 질문에도 그 역할로 답합니다."
            action={
              <Button variant="primary" iconLeft={<Plus size={15} />} onClick={() => navigate('/ai/agents/new')}>
                새 에이전트
              </Button>
            }
          />
        ) : (
          <ul className="aiagents__list">
            {items.map((a) => (
              <li key={a.id}>
                {/* 정본 D9 Card — 「묶음 하나」라 카드다. 이름·범위·상태·설명·행동이 한자리에
                    있어야 무엇인지 알 수 있다. 바탕만 이 화면에서 `--paper` 로 되돌린다(아래 CSS). */}
                <Card headless className={`aiagents__card${a.status === 'STOPPED' ? ' is-stopped' : ''}`}>
                <div className="aiagents__head">
                  <span className="aiagents__nm">{a.name}</span>
                  {a.scope === 'TENANT' ? (
                    <Badge variant="info" size="sm">전사</Badge>
                  ) : (
                    <Badge size="sm">내 것</Badge>
                  )}
                  {a.status === 'STOPPED' && <Badge variant="warning" size="sm">중지됨</Badge>}
                </div>

                {a.description && <p className="aiagents__desc">{a.description}</p>}

                <div className="aiagents__meta">
                  <span className="aiagents__metaitem">
                    {a.scope === 'TENANT' ? <Building2 size={13} aria-hidden /> : <User size={13} aria-hidden />}
                    {a.scope === 'TENANT' ? '회사 전체' : '나만'}
                  </span>
                  {isAiModelAlias(a.modelAlias) && (
                    <span className="aiagents__metaitem">{AI_MODEL_ALIAS_LABEL[a.modelAlias].label}</span>
                  )}
                  {a.starters.length > 0 && (
                    <span className="aiagents__metaitem">시작 질문 {a.starters.length}</span>
                  )}
                  {/* ★ 「전부」는 굳이 안 적는다 — 모두에게 늘 같은 값은 자리만 먹는다.
                       좁혀 둔 것만 보이면 「이 에이전트는 여기만 본다」가 한눈에 갈린다. */}
                  {a.toolDomains.length > 0 && (
                    <span className="aiagents__metaitem">{domainLabels(a.toolDomains)}</span>
                  )}
                </div>

                <div className="aiagents__acts">
                  <Button
                    size="sm"
                    variant="primary"
                    iconLeft={<MessageSquare size={13} />}
                    disabled={a.status !== 'ACTIVE'}
                    onClick={() => pickAgent(a)}
                  >
                    이 에이전트로 대화
                  </Button>
                  {a.canEdit && (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        iconLeft={<Pencil size={13} />}
                        onClick={() => navigate(`/ai/agents/${a.id}`)}
                      >
                        수정
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        iconLeft={a.status === 'ACTIVE' ? <Square size={13} /> : <Play size={13} />}
                        onClick={() => void toggleStatus(a)}
                      >
                        {a.status === 'ACTIVE' ? '중지' : '다시 시작'}
                      </Button>
                    </>
                  )}
                </div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
